/**
 * useOfflineCache - Caches tech job and customer data to localStorage
 *
 * When the browser is ONLINE and data is available, snapshots are saved
 * to localStorage so techs can still view their jobs and customers when
 * connectivity drops (e.g., remote site, poor mobile signal).
 *
 * The cache is scoped to the logged-in tech's ID to prevent data leaks.
 *
 * localStorage keys:
 * - offline_cache_jobs_{techId}: Cached jobs array
 * - offline_cache_customers: Cached customers array
 * - offline_cache_updated: ISO timestamp of last cache update
 */

import { useState, useEffect } from 'react';
import { Job, Customer } from '@/types/schema';

const CACHE_JOBS_PREFIX = 'offline_cache_jobs_';
const CACHE_CUSTOMERS_KEY = 'offline_cache_customers';
const CACHE_UPDATED_KEY = 'offline_cache_updated';

// Max entries to cache (keep localStorage lean)
const MAX_CACHED_JOBS = 100;
const MAX_CACHED_CUSTOMERS = 200;

interface OfflineCacheState {
  /** Cached jobs for the current tech */
  cachedJobs: Job[];
  /** Cached customers */
  cachedCustomers: Customer[];
  /** ISO timestamp of when cache was last updated */
  lastUpdated: string | null;
  /** Whether the cache has data available */
  hasCachedData: boolean;
}

/**
 * Safely write to localStorage with size protection.
 * If the write fails (e.g., quota exceeded), it silently fails.
 */
function safeSetItem(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (e) {
    console.warn('[OfflineCache] localStorage write failed (quota?):', e);
    return false;
  }
}

/**
 * Save jobs to the offline cache.
 * Only stores essential fields to minimize storage size.
 */
export function cacheJobsForTech(techId: string, jobs: Job[]): void {
  if (!techId || !jobs.length) return;

  // Trim to essential fields to save space
  const slimJobs = jobs.slice(0, MAX_CACHED_JOBS).map(job => ({
    id: job.id,
    client: job.client,
    customerId: job.customerId,
    address: job.address,
    loc: job.loc,
    date: job.date,
    time: job.time,
    duration: job.duration,
    type: job.type,
    jobType: job.jobType,
    status: job.status,
    techIds: job.techIds,
    techId: job.techId,
    checklist: job.checklist,
    checkpoints: job.checkpoints,
    vault: job.vault,
    criticalNotes: job.criticalNotes,
    lat: job.lat,
    lng: job.lng,
    sos: job.sos,
    sosReason: job.sosReason,
    flagged: job.flagged,
    flagReason: job.flagReason,
    priority: job.priority,
    createdAt: job.createdAt,
    startedAt: job.startedAt,
    completedAt: job.completedAt,
    plannedMaterials: job.plannedMaterials,
    usedMaterials: job.usedMaterials,
  }));

  const key = CACHE_JOBS_PREFIX + techId;
  const success = safeSetItem(key, JSON.stringify(slimJobs));
  if (success) {
    safeSetItem(CACHE_UPDATED_KEY, new Date().toISOString());
  }
}

/**
 * Save customers to the offline cache.
 * Only stores essential fields.
 */
export function cacheCustomers(customers: Customer[]): void {
  if (!customers.length) return;

  const slimCustomers = customers.slice(0, MAX_CACHED_CUSTOMERS).map(c => ({
    id: c.id,
    firstName: c.firstName,
    lastName: c.lastName,
    name: c.name,
    phone: c.phone,
    email: c.email,
    address: c.address,
    city: c.city,
    accessInstructions: c.accessInstructions,
    vault: c.vault,
    siteNotes: c.siteNotes,
  }));

  const success = safeSetItem(CACHE_CUSTOMERS_KEY, JSON.stringify(slimCustomers));
  if (success) {
    safeSetItem(CACHE_UPDATED_KEY, new Date().toISOString());
  }
}

/**
 * Retrieve cached jobs for a tech.
 */
export function getCachedJobs(techId: string): Job[] {
  if (!techId) return [];

  try {
    const raw = localStorage.getItem(CACHE_JOBS_PREFIX + techId);
    if (!raw) return [];
    return JSON.parse(raw) as Job[];
  } catch {
    return [];
  }
}

/**
 * Retrieve cached customers.
 */
function getCachedCustomers(): Customer[] {
  try {
    const raw = localStorage.getItem(CACHE_CUSTOMERS_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as Customer[];
  } catch {
    return [];
  }
}

/**
 * Hook providing access to offline-cached tech data.
 *
 * Usage:
 * ```tsx
 * const { cachedJobs, cachedCustomers, hasCachedData } = useOfflineCache(techId);
 * ```
 */
export function useOfflineCache(techId: string): OfflineCacheState {
  const [cachedJobs, setCachedJobs] = useState<Job[]>([]);
  const [cachedCustomers, setCachedCustomers] = useState<Customer[]>([]);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  useEffect(() => {
    setCachedJobs(getCachedJobs(techId));
    setCachedCustomers(getCachedCustomers());
    setLastUpdated(localStorage.getItem(CACHE_UPDATED_KEY));
  }, [techId]);

  return {
    cachedJobs,
    cachedCustomers,
    lastUpdated,
    hasCachedData: cachedJobs.length > 0 || cachedCustomers.length > 0,
  };
}
